import { MessageType } from 'antd/lib/message'
import { CreateFolderProps, FolderClient, isTableOrBoard } from 'api/clients/FolderClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { NewInlineOption } from 'components/CreateInlineMenu/types'
import { useIsBlaze } from 'hooks/useIsBlaze'
import { useCallback, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { AlmanacFile } from 'types'
import { reportError } from 'utils/error'
import { getFolderPath } from 'utils/urlUtils'

interface Props {
  onClose: () => void
  chosenOption: NewInlineOption | string
  query: string
  doNotRedirect?: boolean
  setShowCreateMenu: (show: boolean) => void
  options?: { pinned?: boolean; shared?: boolean }
  onSuccess?: (folder: AlmanacFile) => void
  workspaceKey: string | undefined
}

export const useCreateFolder = ({
  onClose,
  onSuccess,
  chosenOption,
  query,
  options,
  doNotRedirect,
  setShowCreateMenu,
  workspaceKey,
}: Props) => {
  const loadingMessageRef = useRef<MessageType | undefined>()
  const history = useHistory()
  const isBlaze = useIsBlaze()
  const thing = chosenOption === NewInlineOption.Database && isBlaze ? 'project' : 'folder'

  const showCreatingToastMessage = useCallback(() => {
    loadingMessageRef.current = showToastMessage(`Creating a ${thing}...`, 'loading', undefined, 0)
  }, [thing])

  const hideCreatingToastMessage = useCallback(() => {
    loadingMessageRef.current?.()
  }, [])

  const createFolder = async (createAnother: boolean = false) => {
    if (!workspaceKey) return

    const handbookMode = chosenOption === 'handbook'
    const databaseMode = chosenOption === 'board_view' || chosenOption === 'table_view'
    const defaultView: CreateFolderProps['defaultView'] = isTableOrBoard(chosenOption) ? chosenOption : 'table_view'
    const title = query.trim() || 'Untitled'

    showCreatingToastMessage()
    onClose()

    const response = await FolderClient.createFolder({
      title,
      workspaceKey: workspaceKey,
      createFolderViewEvent: true,
      defaultView,
      handbookMode,
      databaseMode,
      ...options,
    })

    if (response && response.type === SUCCESS_RESPONSE_TYPE) {
      if (createAnother) setShowCreateMenu(true)
      hideCreatingToastMessage()
      onSuccess?.(response.data.folder)

      const handbook = response.data.folder.handbook
      !!handbook && !doNotRedirect && history.push(getFolderPath({ folderSlug: handbook.seo_string }))
    } else {
      showErrorMessageToast('Something went wrong 😢')
      reportError(new Error(`Error while creating a new ${thing}`), { metaData: { response } })
    }
  }

  return { createFolder }
}
